// Creighton University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Creighton's Common Data Set 2025-2026, posted as one PDF per section on
//    creighton.edu: Section C (C1 applicants, admits and enrollees for Fall
//    2025 with the residency table, C21/C22 plans) and Section G (G1/G5
//    2026-2027 costs). The PDF text layer garbles some fonts and check
//    boxes; the totals are the residency table's total column, which matches
//    the sums of the C1 rows. C21 gives no early decision counts and C22 no
//    early action counts.
//  - Creighton Admissions: Application Dates and Deadlines (the undergraduate
//    dates, which Creighton says apply to the following academic year) and
//    Undergraduate and Transfer Admission Requirements (test-optional policy,
//    direct-entry nursing).
//  - College Scorecard API, school id 181002 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Creighton Common Data Set 2025-2026, Section C',
        url: 'https://www.creighton.edu/sites/default/files/CDS_2025-2026_C.pdf',
        read: '2026-10-01',
    },
    cdsG: {
        label: 'Creighton Common Data Set 2025-2026, Section G',
        url: 'https://www.creighton.edu/sites/default/files/CDS_2025-2026_G.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Creighton Admissions, Application Dates and Deadlines',
        url: 'https://www.creighton.edu/admissions/dates-deadlines',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Creighton Admissions, Undergraduate and Transfer Admission Requirements',
        url: 'https://www.creighton.edu/admissions/admissions-requirements/undergrad-transfer',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?181002-Creighton-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'creighton-university',
    kbId: 'creighton_university',
    name: 'Creighton University',
    shortName: 'Creighton',
    location: 'Omaha, NE',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Creighton brand guide); decoration only.
    branding: {
        primary: '#0054A6',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/creighton_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 6389,
        admitted: 5888,
        enrolled: 1248,
        source: 'cds',
        where: 'section C1, residency table totals',
        residency: [
            { group: 'In-state (Nebraska)', applied: 1005, admitted: 915 },
            { group: 'Out-of-state', applied: 5237, admitted: 4845 },
            { group: 'International', applied: 145, admitted: 126 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Creighton\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Creighton has a non-binding Early Action round and no early decision plan. The Common Data Set gives no early action counts, so there is no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Creighton has no minimum GPA or test score and reviews the whole application. One program asks for more:',
        rows: [
            { school: 'College of Nursing (four-year direct entry)', requirement: 'A high school chemistry course with a C or better.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action', value: 'November 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By December 15', binding: false },
            { plan: 'Priority scholarship deadline', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Scholarship deadline', deadline: 'March 15', decision: null, binding: false },
        ],
        note: 'The application opens August 1. Applying by December 1 also lets you apply for honors and leadership programs and other scholarships, whose materials are due January 10. After March 15, scholarships are awarded on a rolling basis. The FAFSA preferred deadline is January 15. Creighton publishes these dates for every year; it names no separate final application deadline.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Creighton is test-optional: if you choose the test-optional path, your scores are not considered in the admission review, and you are not penalized for admission or merit scholarships. If you send scores, Creighton superscores the ACT and SAT. Every enrolling student must send official ACT or SAT scores for advising.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 50580 },
            { item: 'Required fees', amount: 2322 },
            { item: 'Food and housing (on campus)', amount: 14930 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Transportation', amount: 1000 },
            { item: 'Other expenses', amount: 2500 },
        ],
        total: 72532,
        note: 'For a full-time undergraduate living on campus, fall and spring.',
        source: 'cdsG',
        where: 'sections G1 and G5',
        netPrice: {
            average: 32456,
            byIncome: [
                { income: '$0 to $30,000', amount: 22787 },
                { income: '$30,001 to $48,000', amount: 23888 },
                { income: '$48,001 to $75,000', amount: 24431 },
                { income: '$75,001 to $110,000', amount: 27599 },
                { income: 'Over $110,000', amount: 36758 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Creighton\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
